import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Drawer, Modal } from 'antd';
import { isMobile, toggleGlobalScroll } from '@/utils';
import styles from './index.module.less';

/**
 * 公共组件：PC端弹窗、移动端抽屉 【统一了常见的两者不同的props参数，以modal为准】
 * 内部处理打开时禁止滑动
 * @ref 内部的ref: modalRef
 * @param open
 * @param wrapClassName drawer和modal共用 使用媒体查询分开处理，或者modal和drawer本身的class就不同
 * @param closable
 * @param closeIcon 默认为×
 * @param title 默认 null
 * @param footer 默认 null
 * @param destroyOnClose
 * @param width
 * @param height 默认 'fit-content'
 * @param centered 默认true
 * @param onCancel
 * @param onOk
 * @param mask 默认 true
 * @param maskClosable 默认true
 * @param placement 默认 bottom
 * @param modalProps
 * @param drawerProps
 * @param zIndex
 * @param getContainer
 * @param disabledToggleScroll
 */
const InstaModalDrawer = forwardRef(
  (
    {
      open,
      wrapClassName = '',
      width,
      height = 'fit-content',
      closable,
      placement = 'bottom',
      closeIcon = '×',
      mask = true,
      maskClosable = true,
      destroyOnClose,
      centered = true,
      title = null,
      footer = null,
      modalProps,
      drawerProps,
      onOk,
      onCancel,
      zIndex,
      getContainer,
      disabledToggleScroll = false,
      ...props
    },
    ref
  ) => {
    const modalRef = useRef(null);

    useImperativeHandle(ref, () => ({
      modalRef,
    }));

    const ModalComponent = isMobile() ? Drawer : Modal;

    const componentProps = useMemo(() => {
      const _drawerProps = {
        open,
        placement,
        onClose: onCancel,
        title,
        footer,
        closable,
        height,
        width,
        closeIcon,
        mask,
        maskClosable,
        destroyOnClose,
        zIndex,
        getContainer,
        ...props,
        ...drawerProps,
        className: [styles.modalWrap, wrapClassName, props.className, drawerProps?.className ?? ''].join(' '),
      };

      const _modalProps = {
        open,
        placement,
        title,
        footer,
        closable,
        height,
        width,
        closeIcon,
        mask,
        maskClosable,
        destroyOnClose,
        centered,
        onCancel,
        onOk,
        zIndex,
        getContainer,
        keyboard: false,
        ...props,
        ...modalProps,
        wrapClassName: [styles.modalWrap, props.className, wrapClassName].join(' '),
      };

      return isMobile() ? _drawerProps : _modalProps;
    }, [
      centered,
      closable,
      closeIcon,
      destroyOnClose,
      drawerProps,
      footer,
      height,
      mask,
      maskClosable,
      modalProps,
      onCancel,
      onOk,
      open,
      placement,
      title,
      width,
      wrapClassName,
      zIndex,
      getContainer,
      props,
    ]);

    useEffect(() => {
      !disabledToggleScroll && toggleGlobalScroll(open);
    }, [open]);

    return (
      <ModalComponent {...componentProps} ref={modalRef}>
        {props.children}
      </ModalComponent>
    );
  }
);

InstaModalDrawer.displayName = 'InstaModalDrawer';

export default InstaModalDrawer;
